/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useSyncExternalStore } from "react";
import { type Participant } from "livekit-client";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@vector-im/compound-web";
import { participantHealth } from "./participantHealth";
import styles from "./ConnectionWarning.module.css";

/** Quality belongs to this participant, independently of their camera track. */
export function ConnectionWarning({
  participant,
  name,
  focusable = true,
}: {
  participant?: Participant | null;
  name: string;
  focusable?: boolean;
}) {
  const { t } = useTranslation();
  const health = participant ? participantHealth(participant) : undefined;
  const quality = useSyncExternalStore(
    health?.subscribe ?? (() => () => {}),
    health?.snapshot ?? (() => null),
  );
  if (!quality) return null;
  const label = `${name}: ${t(`instance.ui.connection_${quality}`)}`;
  return (
    <Tooltip
      label={label}
      placement="bottom"
      isTriggerInteractive={false}
      nonInteractiveTriggerTabIndex={focusable ? 0 : -1}
    >
      <span className={styles.badge} role="img" aria-label={label}>
        <span aria-hidden="true">!</span>
      </span>
    </Tooltip>
  );
}
